import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * SEO helper that mutates document head for React 19.
 * Sets: title, description, canonical, Open Graph, Twitter Card, and JSON-LD schema.
 */
export default function Seo({
  title,
  description,
  canonical,
  schema,
  image, // absolute OG image URL
  type = "website", // og:type — "website" | "article" | "product"
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (value == null) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.content = value;
    };

    setMeta("name", "description", description);
    setMeta("property", "og:site_name", "B&L Rentals");
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", image);
    setMeta("property", "og:locale", "en_US");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setMeta("name", "robots", "index, follow, max-image-preview:large");

    if (canonical) {
      let l = document.querySelector('link[rel="canonical"]');
      if (!l) { l = document.createElement("link"); l.rel = "canonical"; document.head.appendChild(l); }
      l.href = canonical;
    }

    let scriptEl;
    if (schema) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.text = JSON.stringify(schema);
      scriptEl.setAttribute("data-seo-schema", "true");
      document.head.appendChild(scriptEl);
    }
    return () => {
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [title, description, canonical, schema, image, type]);
  return null;
}

/** Visual breadcrumbs + BreadcrumbList JSON-LD schema for Google rich results. */
export function Breadcrumbs({ items }) {
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const listItems = items
      .filter((it) => it.to || it === items[items.length - 1])
      .map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.label,
        ...(it.to ? { item: `${origin}${it.to}` } : {}),
      }));
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: listItems,
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-breadcrumb-schema", "true");
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, [items]);

  return (
    <nav aria-label="Breadcrumb" className="text-xs text-[#AEB9C9] mb-4" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1">
            {it.to ? (
              <Link to={it.to} className="hover:text-[#E2AB20]">{it.label}</Link>
            ) : (
              <span className="text-[#E5EAF2]" aria-current="page">{it.label}</span>
            )}
            {i < items.length - 1 && <span className="text-[#8896AC]">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
